import React, { useState, useEffect } from 'react';
import { createClient, getAllClients } from '../services/api';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';

// Mock data for clients and dealers
const mockDealers = [
    { id: 'd1', name: 'Dealer One' },
    { id: 'd2', name: 'Dealer Two' },
    { id: 'd3', name: 'Dealer Three' },
];
// Removed initialClients in favor of API data

const ManagementClients = () => {
    const [clients, setClients] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editClient, setEditClient] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteClient, setDeleteClient] = useState(null);

    // Add Client
    const [newClient, setNewClient] = useState({
        name: '',
        address: '',
        email: '',
        phoneNo: '',
        username: '',
        MSISDN: '',
        loginID: '',
        password: '',
        pinCode: ''
    });

    const fetchClients = async () => {
        try {
            const data = await getAllClients();
            if (Array.isArray(data)) {
                setClients(data);
            } else if (data && Array.isArray(data.content)) {
                setClients(data.content);
            } else {
                console.error("Unknown client data format", data);
            }
        } catch (error) {
            console.error("Failed to fetch clients", error);
        }
    };

    const handleAddClient = async () => {
        // Validation
        const required = ['name', 'username', 'MSISDN', 'loginID', 'password', 'pinCode'];
        const missing = required.filter(field => !newClient[field]);
        if (missing.length > 0) {
            alert(`Please fill in all mandatory fields: ${missing.join(', ')}`);
            return;
        }

        try {
            const payload = {
                ...newClient,
                address: newClient.address || "--",
                email: newClient.email || "--",
                phoneNo: newClient.phoneNo || "--"
            };

            await createClient(payload);
            await fetchClients();

            // For now, we won't update the local list as it depends on a fetch we aren't doing yet,
            // or we could optimistically add it if we knew the ID.
            // Let's just reset and close.
            setShowAddModal(false);
            setNewClient({
                name: '',
                address: '',
                email: '',
                phoneNo: '',
                username: '',
                MSISDN: '',
                loginID: '',
                password: '',
                pinCode: ''
            });
            alert('Client added successfully!');
        } catch (error) {
            console.error('Failed to create client:', error);
            alert('Failed to create client. Please try again.');
        }
    };

    // Edit Client
    const handleEditClient = () => {
        setClients(clients.map(c => c.id === editClient.id ? editClient : c));
        setShowEditModal(false);
        setEditClient(null);
    };

    // Delete Client
    const handleDeleteClient = () => {
        setClients(clients.filter(c => c.id !== deleteClient.id));
        setShowDeleteModal(false);
        setDeleteClient(null);
    };

    // Assign/Reassign Dealer
    const handleAssignDealer = (clientId, dealerId) => {
        setClients(clients.map(c => c.id === clientId ? { ...c, dealerId } : c));
    };



    useEffect(() => {
        fetchClients();
    }, []);

    // Filter clients based on search query
    const filteredClients = clients.filter(client => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            client.name?.toLowerCase().includes(query) ||
            client.username?.toLowerCase().includes(query)
        );
    });

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8 flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Client Management</h1>
                <Button onClick={() => setShowAddModal(true)} variant="primary">Add Client</Button>
            </div>

            {/* Search Input */}
            <div className="mb-4">
                <div className="relative">
                    <input
                        type="text"
                        className="w-full md:w-96 border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Search clients by name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <svg
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <svg className="h-5 w-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    )}
                </div>
                {searchQuery && (
                    <p className="text-sm text-gray-500 mt-2">
                        Found {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
                    </p>
                )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Dealer</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredClients.length === 0 && searchQuery ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                    No clients found matching "{searchQuery}"
                                </td>
                            </tr>
                        ) : (
                            filteredClients.map(client => (
                                <tr key={client.id || client.username}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex flex-col">
                                            <span className="font-medium">{client.name}</span>
                                            <span className="text-xs text-gray-500">{client.username}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{client.email || client.contact || '--'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{client.phoneNo || client.phone || '--'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                        <select
                                            className="border rounded px-2 py-1 text-sm"
                                            value={client.dealerId || ''}
                                            onChange={e => handleAssignDealer(client.id, e.target.value)}
                                        >
                                            <option value="">Unassigned</option>
                                            {mockDealers.map(dealer => (
                                                <option key={dealer.id} value={dealer.id}>{dealer.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                        <Button size="sm" variant="outline" onClick={() => { setEditClient(client); setShowEditModal(true); }}>Edit</Button>
                                        <Button size="sm" variant="danger" onClick={() => { setDeleteClient(client); setShowDeleteModal(true); }}>Delete</Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add Client Modal */}
            {showAddModal && (
                <Modal onClose={() => setShowAddModal(false)} title="Add Client">
                    <div className="space-y-4 max-h-[70vh] overflow-y-auto p-1">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="col-span-1 md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Client Name"
                                    value={newClient.name}
                                    onChange={e => setNewClient({ ...newClient, name: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Username"
                                    value={newClient.username}
                                    onChange={e => setNewClient({ ...newClient, username: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Login ID *</label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Login ID"
                                    value={newClient.loginID}
                                    onChange={e => setNewClient({ ...newClient, loginID: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                                <input
                                    type="password"
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Password"
                                    value={newClient.password}
                                    onChange={e => setNewClient({ ...newClient, password: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Pin Code *</label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Pin Code"
                                    value={newClient.pinCode}
                                    onChange={e => setNewClient({ ...newClient, pinCode: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">MSISDN *</label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="MSISDN"
                                    value={newClient.MSISDN}
                                    onChange={e => setNewClient({ ...newClient, MSISDN: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Phone"
                                    value={newClient.phoneNo}
                                    onChange={e => setNewClient({ ...newClient, phoneNo: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email (Optional)</label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Email"
                                    value={newClient.email}
                                    onChange={e => setNewClient({ ...newClient, email: e.target.value })}
                                />
                            </div>

                            <div className="col-span-1 md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Address (Optional)</label>
                                <input
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Address"
                                    value={newClient.address}
                                    onChange={e => setNewClient({ ...newClient, address: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 pt-4 border-t">
                            <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
                            <Button variant="primary" onClick={handleAddClient}>Add Client</Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Edit Client Modal */}
            {showEditModal && editClient && (
                <Modal onClose={() => setShowEditModal(false)} title="Edit Client">
                    <div className="space-y-4">
                        <input
                            className="w-full border rounded px-3 py-2"
                            placeholder="Client Name"
                            value={editClient.name}
                            onChange={e => setEditClient({ ...editClient, name: e.target.value })}
                        />
                        <input
                            className="w-full border rounded px-3 py-2"
                            placeholder="Contact Email"
                            value={editClient.contact}
                            onChange={e => setEditClient({ ...editClient, contact: e.target.value })}
                        />
                        <input
                            className="w-full border rounded px-3 py-2"
                            placeholder="Phone Number"
                            value={editClient.phone}
                            onChange={e => setEditClient({ ...editClient, phone: e.target.value })}
                        />
                        <select
                            className="w-full border rounded px-3 py-2"
                            value={editClient.dealerId || ''}
                            onChange={e => setEditClient({ ...editClient, dealerId: e.target.value })}
                        >
                            <option value="">Assign Dealer (optional)</option>
                            {mockDealers.map(dealer => (
                                <option key={dealer.id} value={dealer.id}>{dealer.name}</option>
                            ))}
                        </select>
                        <div className="flex justify-end space-x-2">
                            <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
                            <Button variant="primary" onClick={handleEditClient}>Save</Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Delete Client Modal */}
            {showDeleteModal && deleteClient && (
                <Modal onClose={() => setShowDeleteModal(false)} title="Delete Client">
                    <div className="space-y-4">
                        <p>Are you sure you want to delete <span className="font-semibold">{deleteClient.name}</span>?</p>
                        <div className="flex justify-end space-x-2">
                            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                            <Button variant="danger" onClick={handleDeleteClient}>Delete</Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ManagementClients; 